import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { GooglePlacesLocation } from "./GooglePlacesLocation";
import { GooglePlacesAddress } from "./GooglePlacesAddress";
import { validateAddressDistance } from "@/utils/distanceUtils";

const tradeSpecialties = [
  "plumbing", "electrical", "carpentry", "hvac", "painting", "roofing", 
  "masonry", "flooring", "landscaping", "cleaning", "general_repair", 
  "locksmith", "appliance_repair", "pest_control", "window_installation"
];

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  address?: string;
  priority: string;
  required_specialties?: string[];
  budget_min?: number;
  budget_max?: number;
  required_date?: string;
  status: string;
}

interface EditJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  onJobUpdated: () => void;
}

export const EditJobDialog = ({ open, onOpenChange, job, onJobUpdated }: EditJobDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [preferredDate, setPreferredDate] = useState<Date>();
  const [preferredTime, setPreferredTime] = useState("");
  const [addressValue, setAddressValue] = useState("");
  const [addressCoords, setAddressCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addressError, setAddressError] = useState<string>("");
  const { toast } = useToast();

  // Initialize form data when job changes
  useEffect(() => {
    if (job && open) {
      setAddressValue(job.location || "");
      
      if (job.required_date) {
        const date = new Date(job.required_date);
        setPreferredDate(date);
        setPreferredTime(format(date, "HH:mm"));
      } else {
        setPreferredDate(undefined);
        setPreferredTime("");
      }
      
      setAddressError("");
      setAddressCoords(null);
    }
  }, [job, open]);

  const handleAddressChange = (value: string) => {
    console.log('🔧 EditJobDialog: handleAddressChange called with:', value);
    setAddressValue(value);
    if (!value.trim()) {
      setAddressError("");
      setAddressCoords(null);
    }
  };

  const handleAddressCoordinatesChange = (coords: { lat: number; lng: number } | null) => {
    console.log('🔧 EditJobDialog: handleAddressCoordinatesChange called with:', coords);
    setAddressCoords(coords);
    setAddressError("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!job) return;
    
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      
      // Combine date and time for required_date
      let requiredDate: string | null = null;
      if (preferredDate && preferredTime) {
        const [hours, minutes] = preferredTime.split(':');
        const combinedDate = new Date(preferredDate);
        combinedDate.setHours(parseInt(hours), parseInt(minutes));
        requiredDate = combinedDate.toISOString();
      }

      const updateData = {
        title: formData.get("title") as string,
        description: formData.get("description") as string,
        location: addressValue,
        address: addressValue,
        priority: formData.get("priority") as string,
        required_specialties: Array.from(document.querySelectorAll('input[name="requiredSpecialties"]:checked')).map(el => (el as HTMLInputElement).value),
        budget_min: formData.get("budgetMin") ? parseFloat(formData.get("budgetMin") as string) : null,
        budget_max: formData.get("budgetMax") ? parseFloat(formData.get("budgetMax") as string) : null,
        required_date: requiredDate,
      };

      // Validate required fields
      if (!updateData.title.trim()) {
        toast({
          title: "Title required",
          description: "Please enter a job title.",
          variant: "destructive",
        });
        return;
      }

      if (!updateData.description.trim()) {
        toast({
          title: "Description required", 
          description: "Please enter a job description.",
          variant: "destructive",
        });
        return;
      }

      if (!addressValue.trim()) {
        toast({
          title: "Address required",
          description: "Please enter an address.",
          variant: "destructive",
        });
        return;
      }

      if (addressError) {
        toast({
          title: "Address validation error",
          description: addressError,
          variant: "destructive",
        });
        return;
      }

      if (!updateData.required_specialties || updateData.required_specialties.length === 0) {
        toast({
          title: "Trade specialties required",
          description: "Please select at least one trade specialty.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("jobs")
        .update(updateData)
        .eq("id", job.id);

      if (error) throw error;

      toast({
        title: "Job updated successfully!",
        description: "Your job details have been updated.",
      });

      onJobUpdated();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error updating job",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target && target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target && target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Job</DialogTitle>
          <DialogDescription>
            Update your job details. Changes will be saved immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  name="title"
                  defaultValue={job.title}
                  placeholder="e.g., Fix leaky kitchen tap"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  defaultValue={job.description}
                  placeholder="Describe what work needs to be done..."
                  className="min-h-[120px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <GooglePlacesAddress
                  key={`edit-job-address-${job?.id}-${open}`}
                  value={addressValue}
                  onChange={handleAddressChange}
                  onCoordinatesChange={handleAddressCoordinatesChange}
                  placeholder="Enter your specific address"
                />
                {addressError && (
                  <p className="text-sm text-red-600 mt-1">{addressError}</p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <Label>Required Trade Specialties *</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-4 border rounded-lg bg-white/90">
                  {tradeSpecialties.map((specialty) => (
                    <label key={specialty} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="requiredSpecialties"
                        value={specialty}
                        defaultChecked={job.required_specialties?.includes(specialty) || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm capitalize">{specialty.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select name="priority" defaultValue={job.priority}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budgetMin">Min Budget (£)</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="budgetMin"
                      name="budgetMin"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={job.budget_min}
                      placeholder="0"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="budgetMax">Max Budget (£)</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="budgetMax"
                      name="budgetMax"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={job.budget_max}
                      placeholder="0"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Preferred Date & Time (Optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !preferredDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {preferredDate ? format(preferredDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={preferredDate}
                        onSelect={setPreferredDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    placeholder="Select time"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Job"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};